"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConversationPlaybackServiceInstance = void 0;
const client_1 = require("@rws-framework/client");
/**
 * Service dedicated to handling audio playback from various sources (MP3, PCM, etc.)
 * Supports both encoded audio formats and raw PCM data
 */
class ConversationPlaybackService extends client_1.RWSService {
    constructor() {
        super(...arguments);
        this.audioContext = null;
        this.masterGain = null;
        this.nextPlayTime = 0;
        this.volume = 100; // 0-100
        this.isSpeaking = false;
    }
    /**
     * Convert base64 string to ArrayBuffer
     */
    base64ToArrayBuffer(b64) {
        const binary = atob(b64);
        const len = binary.length;
        const buf = new ArrayBuffer(len);
        const view = new Uint8Array(buf);
        for (let i = 0; i < len; i++) {
            view[i] = binary.charCodeAt(i);
        }
        return buf;
    }
    /**
     * Create AudioBuffer from audio data (supports both PCM and encoded formats like MP3)
     */
    async createAudioBuffer(arrayBuffer) {
        if (!this.audioContext) {
            throw new Error('AudioContext not initialized');
        }
        try {
            // First try to decode as encoded audio (MP3, WAV, etc.)
            const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
            return audioBuffer;
        }
        catch (error) {
            // Fallback to raw PCM decoding for backward compatibility
            console.warn('Failed to decode as audio file, trying raw PCM:', error);
            return this.createPCMAudioBuffer(arrayBuffer);
        }
    }
    /**
     * Create AudioBuffer from Raw PCM ArrayBuffer (legacy method)
     */
    createPCMAudioBuffer(arrayBuffer) {
        if (!this.audioContext) {
            throw new Error('AudioContext not initialized');
        }
        const dataView = new DataView(arrayBuffer);
        const sampleCount = arrayBuffer.byteLength / 2; // 16-bit samples
        const audioBuffer = this.audioContext.createBuffer(1, // mono channel
        sampleCount, 16000 // 16kHz sample rate
        );
        const channelData = audioBuffer.getChannelData(0);
        for (let i = 0; i < sampleCount; i++) {
            const int16 = dataView.getInt16(i * 2, true); // little endian
            channelData[i] = int16 / 32768; // normalize to [-1, 1]
        }
        return audioBuffer;
    }
    /**
     * Single shared gain so volume changes affect chunks that are already scheduled
     */
    getMasterGain() {
        if (!this.audioContext) {
            throw new Error('AudioContext not initialized');
        }
        if (!this.masterGain) {
            this.masterGain = this.audioContext.createGain();
            this.masterGain.gain.value = this.volume / 100;
            this.masterGain.connect(this.audioContext.destination);
        }
        return this.masterGain;
    }
    /**
     * Play audio chunk with proper timing
     */
    playChunk(audioBuffer) {
        if (!this.audioContext) {
            throw new Error('AudioContext not initialized');
        }
        const source = this.audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(this.getMasterGain());
        // If we're behind, play immediately
        if (this.nextPlayTime < this.audioContext.currentTime) {
            this.nextPlayTime = this.audioContext.currentTime;
        }
        source.start(this.nextPlayTime);
        this.nextPlayTime += audioBuffer.duration;
        // Handle end of playback
        source.onended = () => {
            var _a;
            // Check if this was the last chunk (simple heuristic)
            if (this.nextPlayTime <= this.audioContext.currentTime + 0.1) {
                this.isSpeaking = false;
                (_a = this.onSpeakingChanged) === null || _a === void 0 ? void 0 : _a.call(this, false);
            }
        };
    }
    async playAudioFromBase64(base64Audio) {
        var _a, _b;
        try {
            this.isSpeaking = true;
            (_a = this.onSpeakingChanged) === null || _a === void 0 ? void 0 : _a.call(this, true);
            // Initialize AudioContext if not already done
            if (!this.audioContext) {
                this.audioContext = new AudioContext();
                this.nextPlayTime = 0;
            }
            // Convert base64 to ArrayBuffer and create AudioBuffer
            const arrayBuffer = this.base64ToArrayBuffer(base64Audio);
            const audioBuffer = await this.createAudioBuffer(arrayBuffer);
            this.playChunk(audioBuffer);
        }
        catch (error) {
            console.error('Failed to play audio:', error);
            this.isSpeaking = false;
            (_b = this.onSpeakingChanged) === null || _b === void 0 ? void 0 : _b.call(this, false);
            throw error;
        }
    }
    /**
     * Set playback volume (0-100)
     */
    setVolume(volume) {
        this.volume = Math.max(0, Math.min(100, Number(volume) || 0));
        if (this.masterGain) {
            this.masterGain.gain.value = this.volume / 100;
        }
    }
    /**
     * Get current volume (0-100)
     */
    getVolume() {
        return this.volume;
    }
    /**
     * Check if currently speaking
     */
    getIsSpeaking() {
        return this.isSpeaking;
    }
    /**
     * Stop current playback
     */
    stopPlayback() {
        var _a;
        if (this.audioContext) {
            // Close and recreate AudioContext to stop all sources
            this.audioContext.close();
            this.audioContext = null;
            this.masterGain = null;
            this.nextPlayTime = 0;
        }
        this.isSpeaking = false;
        (_a = this.onSpeakingChanged) === null || _a === void 0 ? void 0 : _a.call(this, false);
    }
    /**
     * Pause playback (queued chunks keep their schedule on the suspended context)
     */
    async pausePlayback() {
        if (this.audioContext && this.audioContext.state === 'running') {
            await this.audioContext.suspend();
        }
    }
    async resumePlayback() {
        if (this.audioContext && this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }
    }
    /**
     * Interrupt audio playback (for when user starts speaking during agent response)
     */
    interruptPlayback() {
        console.log('🛑 [ConversationPlayback] Interrupting audio playback');
        this.stopPlayback();
    }
    /**
     * Set callback for speaking status changes
     */
    setOnSpeakingChanged(callback) {
        this.onSpeakingChanged = callback;
    }
    /**
     * Remove speaking status callback
     */
    removeOnSpeakingChanged() {
        this.onSpeakingChanged = undefined;
    }
    /**
     * Clean up audio resources
     */
    destroy() {
        this.stopPlayback();
        this.removeOnSpeakingChanged();
    }
}
exports.ConversationPlaybackServiceInstance = ConversationPlaybackService;
exports.default = ConversationPlaybackService.getSingleton();
